/**
 * Utility functions for handling cross-platform filename encoding/decoding
 * Matches the logic in api/s3_upload.php
 */

/**
 * Clean filename by replacing problematic characters with safe Unicode equivalents
 * @param {string} fileName - The filename to clean
 * @returns {string} - The cleaned filename
 */
export function cleanFileName(fileName) {
    if (!fileName) return '';
    
    fileName = fileName.trim();
    
    // Replace problematic characters that could be confused with path separators
    // Forward slash (/) -> Unicode "FRACTION SLASH" (⁄)
    fileName = fileName.replace(/\//g, '⁄');
    
    // Backslash (\) -> Unicode "REVERSE SOLIDUS" (⧵)
    fileName = fileName.replace(/\\/g, '⧵');
    
    return fileName;
}

/**
 * Convert display filename back to original format for user display
 * @param {string} fileName - The encoded filename from storage
 * @returns {string} - The decoded filename for display
 */
export function decodeFileName(fileName) {
    if (!fileName) return '';
    
    // Convert encoded characters back to original for display
    fileName = fileName.replace(/⁄/g, '/');
    fileName = fileName.replace(/⧵/g, '\\');
    
    return fileName;
}

/**
 * Convert display filename to storage format for backend operations
 * @param {string} fileName - The filename as entered by user
 * @returns {string} - The encoded filename for storage
 */
export function encodeFileName(fileName) {
    if (!fileName) return '';
    
    // Convert problematic characters to encoded equivalents
    fileName = fileName.replace(/\//g, '⁄');
    fileName = fileName.replace(/\\/g, '⧵');
    
    return fileName;
}

/**
 * Return a plain-language validation error for one folder-name segment.
 * Structural folder paths must be handled separately so their `/` separators
 * are not mistaken for characters in a user-entered folder name.
 *
 * @param {string} folderName - One user-entered folder leaf name
 * @returns {string} - Empty when valid, otherwise a user-facing error
 */
export function getFolderLeafNameValidationError(folderName) {
    const trimmed = String(folderName ?? '').trim();

    if (!trimmed) {
        return 'Enter a folder name.';
    }

    if (/^\.+$/.test(trimmed)) {
        return 'Folder names cannot consist only of periods.';
    }

    if (Array.from(trimmed).some((character) => {
        const codePoint = character.codePointAt(0);
        return codePoint <= 31 || codePoint === 127;
    })) {
        return 'Folder names cannot contain control characters.';
    }

    return '';
}

/**
 * Normalize one user-entered folder leaf for storage. Slashes are converted to
 * visible Unicode equivalents so a date such as `8/20/26` remains one folder
 * segment, while callers can continue joining real path segments with `/`.
 *
 * @param {string} folderName - One user-entered folder leaf name
 * @returns {string} - A validated storage-safe folder leaf
 * @throws {Error} - When the leaf is blank, dot-only, or contains controls
 */
export function normalizeFolderLeafName(folderName) {
    const error = getFolderLeafNameValidationError(folderName);
    if (error) {
        throw new Error(error);
    }

    return encodeFileName(String(folderName).trim());
}

/**
 * Check if a filename contains encoded characters that need special handling
 * @param {string} fileName - The filename to check
 * @returns {boolean} - True if filename contains encoded characters
 */
export function hasEncodedCharacters(fileName) {
    if (!fileName) return false;
    return fileName.includes('⁄') || fileName.includes('⧵');
}

/**
 * Sanitize filename for safe usage while preserving extension
 * @param {string} fileName - The filename to sanitize
 * @returns {string} - The sanitized filename
 */
export function sanitizeFileName(fileName) {
    if (!fileName) return '';
    
    // First decode any encoded characters for processing
    let decoded = decodeFileName(fileName);
    
    // Remove or replace other problematic characters (but preserve / and \ as they'll be re-encoded)
    decoded = decoded.replace(/[<>:"|?*]/g, '_');
    
    // Re-encode for storage
    return cleanFileName(decoded);
}
